import { appRoute as route, serviceRoute, pageRoute } from "./paths.js";
import { extraServicePages } from "./serviceExtras.js";
import { insightDetailRoute } from "./insightDetails.js";

export const serviceNav = [
  { label: "Agentic AI", slug: "ai-agentic" },
  { label: "Web Development", slug: "web-development" },
  { label: "App Development", slug: "app-development" },
  { label: "UI/UX Design", slug: "ui-ux-design" },
  { label: "Cyber Security", slug: "cyber-security" },
  { label: "Staff Augmentation", slug: "staff-augmentation" },
];

export function getServicePage(slug = "web-development") {
  return content.servicePages[slug] || content.servicePages["web-development"];
}


export const content = {
  brand: "Taldel",
  nav: {
    primary: [
      {
        label: "Services",
        children: serviceNav.map((item) => ({
          label: item.label,
          href: serviceRoute(item.slug),
          slug: item.slug,
        })),
      },
      { label: "Our Portfolios", href: pageRoute("our-portfolios") },
      { label: "Insights", href: pageRoute("insights") },
      { label: "About", href: pageRoute("about-us") },
    ],
    side: [
      { label: "Company", href: route("manifesto") },
      { label: "Delivery", href: route("numbers") },
      { label: "Reviews", href: route("testimonials") },
      { label: "Teams", href: route("team") },
      { label: "FAQs", href: route("faqs") },
      { label: "Contact", href: route("contact") },
    ],
    cta: { label: "Register", href: pageRoute("signup") },
  },
  hero: {
    titleSegments: [
      { text: "Full-stack ", highlight: null },
      { text: "technology", highlight: "primary" },
      { text: " ", highlight: null },
      { text: "partner", highlight: "secondary" },
      { text: " for startups and scaling businesses.", highlight: null },
    ],
    subtitle:
      "Taldel helps startups and growing businesses design, build, and scale software and AI-enabled products — with dedicated offshore engineering teams, AI-vetted and ready to deploy in days.",
    ctaPrimary: "Explore services",
    ctaPrimaryHref: serviceRoute("web-development"),
    ctaSecondary: "Talk to us",
    ctaSecondaryHref: route("contact"),
    tags: [
      "AI Enabled Delivery",
      "Software and Data Solutions",
      "Build Developer Squads",
    ],
    panel: {
      title: "AI delivery intelligence",
      status: "Scanning talent graph",
      gauge: { value: 94, label: "Match quality" },
      capabilities: ["React", "Node.js", "Python", "AI / ML", ".NET", "Cloud"],
      pipeline: ["Source", "AI vet", "Team fit", "Deploy"],
      regions: ["PK", "IN", "EU", "US"],
      waveLabel: "Offshore capacity",
      stats: [
        { label: "Active squads", value: "12" },
        { label: "Deploy window", value: "5 days" },
      ],
    },
  },
  manifesto: {
    watermark: "Taldel",
    badge: "Less talk, more build",
    paragraph:
      "The best software isn't hidden behind layers of marketing speak or buried in endless feature lists that nobody actually reads. We stripped away all the unnecessary noise and clutter so you can see what truly matters, understand it quickly, and move on with your day without wasting time deciphering buzzwords or corporate jargon.",
    cta: "Find Your Stack",
    ctaHref: route("advantage"),
  },
  numbers: {
    badge: "By the Numbers",
    titleSegments: [
      { text: "A delivery model built on ", highlight: null },
      { text: "real", highlight: "primary" },
      { text: " ", highlight: null },
      { text: "results", highlight: "secondary" },
    ],
    subtitle:
      "We've built something that matters. Startups and growing teams trust Taldel to ship their next product. The numbers speak for themselves.",
    cardStack: {
      value: "120+",
      title: "Products shipped with real teams",
      description: "Software, data, and AI builds across every industry",
      tiles: ["JS", "Py", "AI"],
    },
    cardOrbit: {
      value: "94,200+",
      title: "Engineering hours delivered",
      description: "Across dedicated squads and embedded engineers",
    },
    cardRank: {
      value: "89%",
      title: "Clients scale their team",
      description: "Companies that extend or grow their squad after the first build",
      rows: [
        { initials: "AK", label: "Top-Rated Squad", badge: "★" },
        { initials: "SR", label: "Rising Team", badge: "" },
        { initials: "MJ", label: "Most Loved", badge: "" },
      ],
    },
  },
  testimonials: {
    badgePre: "See our 436 reviews on",
    badgeBrand: "Trustpilot",
    titleSegments: [
      { text: "Loved by ", highlight: null },
      { text: "early users", highlight: "primary" },
      { text: ". Built for ", highlight: null },
      { text: "more", highlight: "secondary" },
    ],
    subtitle:
      "A curated network of enterprise-ready and startup-friendly engineering teams, tailored to your use case.",
    items: [
      {
        rating: "4.9/5",
        quote:
          "I've tried half a dozen dev shops over the past year, but none offered the combination of speed, clarity, and code quality like Taldel. From MVP to production, it just works.",
        name: "Olivia Taylor",
        role: "Founder, BlockSavvy",
        initials: "OT",
      },
      {
        rating: "5.0/5",
        quote:
          "The AI-vetted engineers were productive from week one. It felt like extending our own team, not outsourcing — the communication rhythm is genuinely excellent.",
        name: "Emma Wilson",
        role: "CTO, Fintech Startup",
        initials: "EW",
      },
      {
        rating: "4.8/5",
        quote:
          "We scaled from two engineers to a full squad in under a month. The delivery cadence and timezone overlap made it painless for our product team.",
        name: "Lucas Walker",
        role: "VP Engineering, SaaS Platform",
        initials: "LW",
      },
      {
        rating: "5.0/5",
        quote:
          "Taldel rebuilt our data pipeline and shipped an AI feature we'd been putting off for a year. Serious engineering, without the hiring overhead.",
        name: "Sofia Martinez",
        role: "Head of Product, RetailTech",
        initials: "SM",
      },
    ],
  },
  advantage: {
    badge: "Our Advantage",
    titleSegments: [
      { text: "What ", highlight: null },
      { text: "sets", highlight: "primary" },
      { text: " us ", highlight: null },
      { text: "apart", highlight: "secondary" },
    ],
    cards: [
      {
        eyebrow: "Discovery",
        heading: "Find the right team in minutes",
        subtitle:
          "A curated network of enterprise-ready and startup-friendly engineers, tailored to your stack and use case.",
        cta: "Start matching",
        ctaHref: route("contact"),
        stats: [
          { value: "3,200+", label: "Vetted engineers" },
          { value: "140+", label: "Specializations" },
        ],
        icons: [
          { at: 1, glyph: "N", tone: "mono" },
          { at: 4, glyph: "◆", tone: "orange" },
          { at: 7, glyph: "▣", tone: "violet", center: true },
          { at: 15, glyph: "✻", tone: "green" },
          { at: 18, glyph: "❤", tone: "pink" },
        ],
      },
      {
        eyebrow: "Delivery",
        heading: "Ship with a cadence you can trust",
        subtitle:
          "Timezone overlap, clear sprint rhythm, and direct access to your engineers — we run the delivery layer so you stay focused on product.",
        cta: "Start matching",
        ctaHref: route("contact"),
        stats: [
          { value: "5 days", label: "Average deploy window" },
          { value: "12+", label: "Active squads" },
        ],
        icons: [
          { at: 0, glyph: "⚡", tone: "orange" },
          { at: 3, glyph: "◈", tone: "mono" },
          { at: 12, glyph: "✓", tone: "green", center: true },
          { at: 9, glyph: "●", tone: "pink" },
          { at: 16, glyph: "▲", tone: "violet" },
        ],
      },
      {
        eyebrow: "Scale",
        heading: "Grow from one engineer to a squad",
        subtitle:
          "Start small and extend as your roadmap grows. Add specialists, spin up full teams, and scale down just as easily — no hiring machine required.",
        cta: "Start matching",
        ctaHref: route("contact"),
        stats: [
          { value: "89%", label: "Clients extend their team" },
          { value: "1 → 12", label: "Engineer to squad" },
        ],
        icons: [
          { at: 2, glyph: "✦", tone: "violet" },
          { at: 5, glyph: "▤", tone: "mono" },
          { at: 8, glyph: "◉", tone: "pink", center: true },
          { at: 14, glyph: "＋", tone: "green" },
          { at: 19, glyph: "◆", tone: "orange" },
        ],
      },
    ],
  },
  team: {
    badge: "Our Team",
    titleSegments: [
      { text: "The ", highlight: null },
      { text: "people", highlight: "primary" },
      { text: " behind ", highlight: null },
      { text: "Taldel", highlight: "secondary" },
    ],
    subtitle:
      "Full stack, backend, mobile, cloud, data, and AI engineers who've shipped for startups and enterprises alike — ready to join your squad.",
    members: [
      {
        name: "Ayesha Khan",
        role: "Full Stack Developer",
        initials: "AK",
        stack: "react",
        experience: 8,
        tags: ["React", "Node.js", "PostgreSQL"],
      },
      {
        name: "Hamza Riaz",
        role: "Backend Developer",
        initials: "HR",
        stack: "node",
        experience: 7,
        tags: ["Node.js", "Express", "MongoDB"],
      },
      {
        name: "Sara Malik",
        role: "Frontend Developer",
        initials: "SM",
        stack: "typescript",
        experience: 6,
        tags: ["TypeScript", "Next.js", "Tailwind"],
      },
      {
        name: "Usman Tariq",
        role: "DevOps Engineer",
        initials: "UT",
        stack: "docker",
        experience: 9,
        tags: ["Docker", "Kubernetes", "CI / CD"],
      },
      {
        name: "Fatima Noor",
        role: "AI / ML Engineer",
        initials: "FN",
        stack: "pytorch",
        experience: 6,
        tags: ["PyTorch", "LLMs", "MLOps"],
      },
      {
        name: "Ali Hassan",
        role: "Data Engineer",
        initials: "AH",
        stack: "sql",
        experience: 7,
        tags: ["Spark", "Airflow", "Snowflake"],
      },
      {
        name: "Maryam Siddiqui",
        role: "Data Scientist",
        initials: "MS",
        stack: "python",
        experience: 6,
        tags: ["Python", "Pandas", "scikit-learn"],
      },
      {
        name: "Bilal Ahmed",
        role: "Cloud Engineer",
        initials: "BA",
        stack: "aws",
        experience: 8,
        tags: ["AWS", "Terraform", "Serverless"],
      },
      {
        name: "Zainab Ali",
        role: "Mobile Developer",
        initials: "ZA",
        stack: "flutter",
        experience: 5,
        tags: ["Flutter", "React Native", "iOS / Android"],
      },
      {
        name: "Omar Sheikh",
        role: ".NET Developer",
        initials: "OS",
        stack: "dotnet",
        experience: 10,
        tags: ["C#", "ASP.NET Core", "Azure"],
      },
      {
        name: "Hina Rizvi",
        role: "QA Automation Engineer",
        initials: "HN",
        stack: "github",
        experience: 7,
        tags: ["Playwright", "Cypress", "GitHub Actions"],
      },
    ],
  },
  faqs: {
    badge: "FAQs",
    titleSegments: [
      { text: "Questions ", highlight: null },
      { text: "teams", highlight: "primary" },
      { text: " ask ", highlight: null },
      { text: "first", highlight: "secondary" },
    ],
    subtitle: "How pods and staff augmentation work with Taldel — before you pick a model.",
    items: [
      {
        question: "What's the difference between staff augmentation and hiring a pod?",
        answer:
          "Staff augmentation adds individual engineers into your existing team and workflow. A pod is a pre-formed, self-sufficient squad, usually 3–6 people spanning the roles a feature actually needs (backend, frontend, QA, sometimes a PM) — that can run its own slice of the roadmap with less day-to-day management from you.",
      },
      {
        question: "How is a pod structured?",
        answer:
          "A pod is built around the work, not a headcount request. A typical pod might be one senior engineer as tech lead, two mid-level engineers, and a QA/DevOps generalist, sized to own a full feature or workstream end-to-end rather than just filling a ticket queue.",
      },
      {
        question: "How fast can a pod or an augmented engineer start?",
        answer:
          "Individual engineers typically ramp in 5–10 business days. A full pod takes slightly longer to assemble, usually 2–3 weeks, since we're matching roles to your stack and pairing people who've already worked together where possible.",
      },
      {
        question: "Do we manage the pod, or do you?",
        answer:
          "Either. Augmented individuals usually report into your existing leads and ceremonies. A pod can run the same way, or operate with its own internal lead who reports status and blockers up to you, so you get squad-level output without squad-level management overhead.",
      },
      {
        question: "Can we flex the size up or down?",
        answer:
          "Yes. Capacity is reviewed on a regular cadence (weekly or monthly, your call), and seats can be added or dropped with short notice, you're not locked into a headcount for the life of the engagement.",
      },
    ],
  },
  features: {
    title: "How it works",
    subtitle:
      "A technical delivery model for companies that need engineering capacity — without slowing down to build a hiring machine.",
    items: [
      {
        title: "AI vetting pipeline",
        description:
          "Every engineer is evaluated on skills, code quality, and communication before they reach you. You talk to people who can actually do the work.",
      },
      {
        title: "Dedicated engineers & teams",
        description:
          "Add individual contributors or full squads embedded in your stack, tools, and rhythm. Built for ongoing product development.",
      },
      {
        title: "Offshore delivery, done properly",
        description:
          "We run the delivery layer — timezone overlap, clear cadence, direct access to your team. You stay focused on the product.",
      },
    ],
  },
  showcase: {
    title: "For companies building product",
    subtitle:
      "Startups and small tech teams use Taldel to scale engineering offshore — when hiring locally is slow, expensive, or simply not enough.",
    stats: [
      { value: "AI", label: "Technical vetting" },
      { value: "Teams", label: "Engineers or squads" },
      { value: "Global", label: "Offshore delivery" },
    ],
  },
  contact: {
    titleSegments: [
      { text: "Let's talk about your ", highlight: null },
      { text: "technology", highlight: "primary" },
      { text: " ", highlight: null },
      { text: "needs", highlight: "secondary" },
    ],
    intro:
      "Tell us what you are trying to build, improve, automate, or scale. We will help identify whether you need a software solution, data workflow, AI automation, or developer/team support.",
    highlights: [
      {
        label: "Best for",
        text: "Small and mid-sized companies that need practical technology execution.",
      },
      {
        label: "Common needs",
        text: "Software, data, AI automation, internal tools, workflow improvements, and developer teams.",
      },
      {
        label: "Next step",
        text: "A quick conversation to understand the business problem and best-fit solution path.",
      },
    ],
    form: {
      submitLabel: "Send Message",
      supportPlaceholder: "What type of technology support are you looking for?",
      messagePlaceholder: "Tell us what you are trying to build or improve",
      supportOptions: [
        { value: "", label: "What type of technology support are you looking for?" },
        { value: "software", label: "Software development" },
        { value: "data", label: "Data & analytics" },
        { value: "ai", label: "AI automation" },
        { value: "teams", label: "Developer / team support" },
        { value: "other", label: "Other" },
      ],
    },
    success: {
      kicker: "Thank you",
      title: "We have received your details",
      body: "Someone from our team will reach out.",
    },
    email: "hello@taldel.com",
  },
  servicePages: {
    "web-development": {
      title: "Web Development",
      documentTitle: "Taldel — Web Development",
      banner: {
        badge: "Web Development",
        titleSegments: [
          { text: "Web products ", highlight: null },
          { text: "built", highlight: "primary" },
          { text: " to ", highlight: null },
          { text: "scale", highlight: "secondary" },
          { text: ".", highlight: null },
        ],
        subtitle:
          "Design, build, and ship modern web apps with AI-vetted frontend and full-stack engineers — from MVPs and marketing sites to complex SaaS platforms.",
        ctaPrimary: "Start a conversation",
        ctaPrimaryHref: route("contact"),
        ctaSecondary: "See our approach",
        ctaSecondaryHref: serviceRoute("web-development", "approach"),
        tags: ["React & Next.js", "TypeScript", "Node.js", "Design systems"],
      },
      scroll: {
        items: [
          { label: "React & Next.js", accent: "#6c8cff" },
          { label: "TypeScript apps", accent: "#9d6cff" },
          { label: "SaaS dashboards", accent: "#ff6c9c" },
          { label: "Design systems", accent: "#3fbf8f" },
          { label: "API & BFF layers", accent: "#f5c96e" },
          { label: "Performance & SEO", accent: "#6ce0ff" },
          { label: "CMS & marketing sites", accent: "#fb923c" },
          { label: "Web app modernization", accent: "#a78bfa" },
        ],
      },
      approach: {
        badge: "Approach",
        titleSegments: [
          { text: "How we ", highlight: null },
          { text: "build", highlight: "primary" },
          { text: " the ", highlight: null },
          { text: "web", highlight: "secondary" },
        ],
        subtitle:
          "A product-minded web delivery model — clear scope, strong UI craft, and engineers who ship production-ready code.",
        steps: [
          {
            title: "Discover",
            description:
              "Map flows, stack constraints, and success metrics. Align on MVP scope before a single component ships.",
          },
          {
            title: "Assemble",
            description:
              "Pair AI-vetted React/Node engineers with your product rhythm. You interview the people writing the code.",
          },
          {
            title: "Ship",
            description:
              "Iterate in short cycles with reviews, accessibility checks, and deploy-ready PRs — not throwaway prototypes.",
          },
          {
            title: "Scale",
            description:
              "Grow from a feature squad to a full web platform team as traffic, modules, and roadmap expand.",
          },
        ],
      },
      testimonials: {
        badgePre: "Trusted by teams who",
        badgeBrand: "ship on the web",
        titleSegments: [
          { text: "Proof from ", highlight: null },
          { text: "product", highlight: "primary" },
          { text: " teams ", highlight: null },
          { text: "shipping", highlight: "secondary" },
        ],
        subtitle:
          "Founders and engineering leaders use Taldel web development to launch faster — without compromising UX or code quality.",
        items: [
          {
            rating: "4.9/5",
            quote:
              "Taldel rebuilt our customer portal in weeks. Clean React architecture, fast reviews, and a UI that finally matched the product we promised.",
            name: "Olivia Taylor",
            role: "Founder, BlockSavvy",
            initials: "OT",
          },
          {
            rating: "5.0/5",
            quote:
              "Their frontend engineers slotted into our Next.js monorepo immediately. It felt like extending our own web team, not outsourcing.",
            name: "Emma Wilson",
            role: "CTO, Fintech Startup",
            initials: "EW",
          },
          {
            rating: "4.8/5",
            quote:
              "We went from wireframes to a production SaaS dashboard in under a month. Design system discipline made every sprint compounding.",
            name: "Lucas Walker",
            role: "VP Engineering, SaaS Platform",
            initials: "LW",
          },
          {
            rating: "5.0/5",
            quote:
              "Performance, accessibility, and DX were treated as first-class. Our Core Web Vitals and release confidence both jumped.",
            name: "Sofia Martinez",
            role: "Head of Product, RetailTech",
            initials: "SM",
          },
        ],
      },
      faqs: {
        badge: "FAQs",
        titleSegments: [
          { text: "Web ", highlight: null },
          { text: "dev", highlight: "primary" },
          { text: " questions, ", highlight: null },
          { text: "answered", highlight: "secondary" },
        ],
        subtitle: "Straight answers about how Taldel web development engagements work.",
        items: [
          {
            question: "What web development work do you take on?",
            answer:
              "Product UIs, SaaS dashboards, marketing sites, design systems, API/BFF layers, and modernization of legacy frontends — typically React, Next.js, TypeScript, and Node.",
          },
          {
            question: "Can you work inside our existing codebase?",
            answer:
              "Yes. Engineers embed in your repos, design tokens, CI, and review process. We adapt to your stack instead of forcing a greenfield rewrite.",
          },
          {
            question: "How quickly can a web squad start?",
            answer:
              "Most engagements begin within days after discovery and fit checks. Average deploy windows land around five days once roles are confirmed.",
          },
          {
            question: "Do you cover design as well as engineering?",
            answer:
              "We can pair product engineering with design-system and UI craft support, or plug into your existing design team — whichever fits the roadmap.",
          },
          {
            question: "Can we start with one engineer and grow?",
            answer:
              "That’s the default. Start with a specialist or small pod, then add frontend, full-stack, or QA capacity as the product grows.",
          },
        ],
      },
      insights: {
        badge: "Insights",
        titleSegments: [
          { text: "Ideas for ", highlight: null },
          { text: "better", highlight: "primary" },
          { text: " web ", highlight: null },
          { text: "products", highlight: "secondary" },
        ],
        subtitle: "Short reads on frontend delivery, design systems, and shipping web apps faster.",
        items: [
          {
            slug: "why-design-systems-speed-up-web-squads",
            category: "Frontend",
            title: "Why design systems speed up web squads",
            excerpt: "How shared components and tokens cut rework across marketing, app, and admin surfaces.",
            href: insightDetailRoute("why-design-systems-speed-up-web-squads"),
            readTime: "4 min",
            accent: "#6c8cff",
          },
          {
            slug: "shipping-saas-ui-in-weekly-cycles",
            category: "Delivery",
            title: "Shipping SaaS UI in weekly cycles",
            excerpt: "A practical cadence for reviews, accessibility checks, and deploy-ready pull requests.",
            href: insightDetailRoute("shipping-saas-ui-in-weekly-cycles"),
            readTime: "5 min",
            accent: "#3fbf8f",
          },
          {
            slug: "when-to-choose-nextjs-vs-a-spa",
            category: "Stack",
            title: "When to choose Next.js vs. a SPA",
            excerpt: "A simple framework for founders balancing SEO, app UX, and engineering complexity.",
            href: insightDetailRoute("when-to-choose-nextjs-vs-a-spa"),
            readTime: "6 min",
            accent: "#6ce0ff",
          },
        ],
      },
    },
    ...extraServicePages,
  },
  footer: {
    columns: [
      {
        title: "Company",
        links: [
          { label: "About", href: pageRoute("about-us") },
          { label: "Approach", href: route("advantage") },
          { label: "Team", href: route("team") },
          { label: "Careers", href: route("contact") },
        ],
      },
      {
        title: "Capabilities",
        links: [
          { label: "Agentic AI", href: serviceRoute("ai-agentic") },
          { label: "Web Development", href: serviceRoute("web-development") },
          { label: "App Development", href: serviceRoute("app-development") },
          { label: "UI/UX Design", href: serviceRoute("ui-ux-design") },
          { label: "Cyber Security", href: serviceRoute("cyber-security") },
          { label: "Staff Augmentation", href: serviceRoute("staff-augmentation") },
        ],
      },
      {
        title: "Resources",
        links: [
          { label: "Client stories", href: route("testimonials") },
          { label: "Insights", href: pageRoute("insights") },
          { label: "FAQs", href: route("faqs") },
          { label: "Contact", href: route("contact") },
        ],
      },
    ],
    social: [
      { label: "Instagram", href: "https://www.instagram.com/_taldel", icon: "instagram" },
      { label: "LinkedIn", href: "https://www.linkedin.com/company/taldel/", icon: "linkedin" },
      { label: "Facebook", href: "https://www.facebook.com/share/19GH7Mdix8/", icon: "facebook" },
      { label: "X", href: "https://x.com/_taldel", icon: "x" },
    ],
    legal: [
      { label: "Privacy", href: pageRoute("privacy-policy") },
      { label: "Terms", href: pageRoute("terms-and-conditions") },
    ],
    year: new Date().getFullYear(),
  },
};